import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { BookOpen } from "lucide-react";
import ToastContainer, { showToast } from "./Toast";
import { STORAGE_KEYS, readStorage, saveStorage, addActivity, getSchoolSettings, loadActions, saveActions, loadGoals, saveGoals, addActivityAsync } from "./utils";
import StaffSelect from "./StaffSelect";

const TAG = {
  green: "bg-[#eaf8f1] text-[#2d9b68] border-[#cfe8dc]",
  blue: "bg-[#eef6fc] text-[#2d81c1] border-[#cfe3f3]",
  orange: "bg-[#fff4eb] text-[#f47c20] border-[#f7c9a1]",
  yellow: "bg-[#fff7e7] text-[#d58d1f] border-[#f4deab]",
  red: "bg-[#fff0f0] text-[#d95353] border-[#f4c5c5]",
};

function Tag({ cls = "blue", children }) {
  return (
    <span className={`inline-flex items-center gap-[6px] px-[10px] py-[6px] rounded-full text-[11px] font-semibold border whitespace-nowrap ${TAG[cls] || TAG.blue}`}>
      {children}
    </span>
  );
}

function Window({ title, note, headerRight, children }) {
  return (
    <section className="bg-white border border-[#e6ecf3] rounded-[26px] shadow-[0_16px_36px_rgba(19,31,47,.07)] overflow-hidden">
      <div className="px-5 py-[18px] border-b border-[#e6ecf3] flex items-center justify-between gap-3">
        <div>
          <div className="text-[16px] font-semibold text-[#233445] mb-[2px]">{title}</div>
          {note && <div className="text-[12px] text-[#617587]">{note}</div>}
        </div>
        {headerRight && <div className="flex flex-wrap gap-[10px]">{headerRight}</div>}
      </div>
      <div className="p-5">{children}</div>
    </section>
  );
}

const EMPTY_ACTION = { goal: "", status: "", title: "", description: "", owner: "", support: "", startDate: "", endDate: "", dependencies: "" };

function calculateCompletion(actions) {
  const total = actions.length;
  const withGoal = actions.filter(a => a.goal).length;
  const withOwner = actions.filter(a => a.owner).length;
  const withDates = actions.filter(a => a.startDate && a.endDate).length;
  const inProgress = actions.filter(a => a.status === "In Progress").length;
  const withDeps = actions.filter(a => a.dependencies).length;

  let filled = 0;
  if (total > 0) filled++;
  if (total >= 3) filled++;
  if (withGoal >= Math.min(total, 2) && total > 0) filled++;
  if (withOwner >= Math.min(total, 2) && total > 0) filled++;
  if (withDates >= Math.min(total, 2) && total > 0) filled++;
  if (inProgress > 0 || total > 0) filled++;
  if (withDeps > 0) filled++;
  if (total >= 5) filled++;

  return Math.round((filled / 8) * 100);
}

function formatDateRange(start, end) {
  if (start && end) return `${start} → ${end}`;
  if (start) return `Starts ${start}`;
  if (end) return `Ends ${end}`;
  return "No Dates";
}

function statusCls(status) {
  if (status === "Completed") return "green";
  if (status === "In Progress") return "blue";
  if (status === "Delayed") return "red";
  return "orange";
}

export default function ActionPlan() {
  const settings = getSchoolSettings();
  const [actions, setActions] = useState(() => {
    const stored = readStorage(STORAGE_KEYS.actions, null);
    return Array.isArray(stored?.actions) ? stored.actions : [];
  });
  const [goals, setGoals] = useState(() => {
    const stored = readStorage(STORAGE_KEYS.goals, null);
    return Array.isArray(stored?.goals) ? stored.goals : [];
  });
  const [form, setForm] = useState(EMPTY_ACTION);
  const [editingIndex, setEditingIndex] = useState(null);
  const [activeTab, setActiveTab] = useState("entry");
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [quickStatus, setQuickStatus] = useState("Planned");

  useEffect(() => {
    loadActions([]).then(apiActions => { if (apiActions.length > 0) setActions(apiActions); });
    loadGoals([]).then(apiGoals => { if (apiGoals.length > 0) setGoals(apiGoals); });
  }, []);

  function persistActions(list, label = "Action plans saved") {
    saveActions(list);
    addActivityAsync("Action Plans", label, "Saved", "green");
  }

  function handleAddOrUpdate() {
    if (!form.goal || form.goal === "__no_goals__" || !form.status || !form.title.trim() || !form.description.trim()) {
      showToast("Linked Goal, Status, Action Step, and Description are required.", "error");
      return;
    }
    let updated;
    if (editingIndex !== null) {
      updated = actions.map((a, i) => i === editingIndex ? form : a);
      persistActions(updated, "Action item updated");
      showToast("Action item updated.", "success");
    } else {
      updated = [...actions, form];
      persistActions(updated, "Action item added");
      showToast("Action item added.", "success");
    }
    setActions(updated);
    setForm(EMPTY_ACTION);
    setEditingIndex(null);
  }

  function handleEdit(i) {
    setForm(actions[i]);
    setEditingIndex(i);
    setActiveTab("entry");
  }

  function handleDelete(i) {
    const updated = actions.filter((_, idx) => idx !== i);
    persistActions(updated, "Action item deleted");
    setActions(updated);
    showToast("Action item deleted.", "warn");
  }

  function handleSaveDraft() {
    if (!actions.length) { showToast("Add at least one action item before saving draft.", "error"); return; }
    persistActions(actions, "Action plans draft saved");
    showToast("Action plans draft saved.", "success");
  }

  function handleFillSample() {
    const sampleGoals = goals.length ? goals : [
      { title: "Improve teaching consistency across middle grades" },
      { title: "Strengthen parent communication and trust" },
      { title: "Improve review-to-action follow-through" },
    ];
    if (!goals.length) {
      saveGoals(sampleGoals);
      setGoals(sampleGoals);
    }
    const sample = [
      { goal: "Improve teaching consistency across middle grades", status: "Planned", title: "Create classroom observation rubric", description: "Design a clear observation rubric aligned with expected teaching standards.", owner: "Academic Coordinator", support: "Academic Excellence Committee", startDate: "2026-04-15", endDate: "2026-04-30", dependencies: "Principal approval on rubric format" },
      { goal: "Improve teaching consistency across middle grades", status: "In Progress", title: "Schedule monthly classroom observations", description: "Build a structured monthly classroom review cycle for all middle-grade teachers.", owner: "Vice Principal", support: "Academic Coordinator", startDate: "2026-05-01", endDate: "2026-08-31", dependencies: "Observation rubric and reviewer training" },
      { goal: "Strengthen parent communication and trust", status: "Planned", title: "Design parent communication calendar", description: "Create a structured communication rhythm for academic, wellbeing, and progress updates.", owner: "Principal", support: "Parent Engagement Committee", startDate: "2026-04-20", endDate: "2026-05-10", dependencies: "Agreement on reporting format and communication channels" },
    ];
    persistActions(sample, "Sample action plans loaded");
    setActions(sample);
    showToast("Sample Action Plans content loaded.", "success");
  }

  function handleClear() {
    if (!window.confirm("This will clear all saved data in Action Plans. Continue?")) return;
    saveActions([]);
    addActivityAsync("Action Plans", "Module cleared", "Cleared", "red");
    setActions([]);
    setForm(EMPTY_ACTION);
    setEditingIndex(null);
    showToast("Action Plans module cleared.", "warn");
  }

  function handleQuickStatus(e) {
    setQuickStatus(e.target.value);
    setForm(f => ({ ...f, status: e.target.value }));
    showToast(`Status preselected as ${e.target.value}.`, "success");
  }

  const completion = calculateCompletion(actions);
  const inProgress = actions.filter(a => a.status === "In Progress").length;
  const readiness = completion >= 85 ? "High" : completion >= 55 ? "Medium" : "Low";
  const interpretation = completion >= 85
    ? "The school now has a usable execution structure and can move into monitoring with good clarity."
    : completion >= 55
    ? "The school has a decent execution base, but some action items still need stronger ownership, dates, or dependencies."
    : "The school still needs more defined action items before monitoring can become meaningful.";

  const uniqueGoals = [...new Set(actions.map(a => a.goal).filter(Boolean))];
  const uniqueOwners = [...new Set(actions.map(a => a.owner).filter(Boolean))];
  const withDependencies = actions.filter(a => a.dependencies).length;
  const withDates = actions.filter(a => a.startDate || a.endDate).length;

  const ACTION_STATUS_OPTIONS = ["Planned","In Progress","Completed","Delayed"];

  const filteredActions = actions.filter(a =>
    (!searchQuery.trim() || a.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      (a.goal || "").toLowerCase().includes(searchQuery.toLowerCase()) ||
      (a.owner || "").toLowerCase().includes(searchQuery.toLowerCase())) &&
    (!statusFilter || a.status === statusFilter)
  );

  const tabBtn = (tab, label) => (
    <button
      onClick={() => setActiveTab(tab)}
      className={`px-5 py-[10px] rounded-[12px] text-[13px] font-semibold transition-colors ${
        activeTab === tab ? "bg-[#f47c20] text-white" : "bg-white text-[#617587] border border-[#e6ecf3] hover:bg-[#eef6fc] hover:text-[#2d81c1]"
      }`}
    >
      {label}
    </button>
  );

  return (

<>
      <div className="flex-1 min-w-0 flex flex-col border-l-2 border-slate-200 ml-1">

        <div className="flex justify-center pl-10 pr-6 py-6">
          <main className="w-full font-[poppins]">
            <section className="mb-8">
              <div className="text-center px-4 py-6">
                <div className="inline-flex items-center justify-center w-16 h-16 rounded-full mb-6 bg-[#D67C2C]">
                  <BookOpen className="w-8 h-8 text-white" />
                </div>
                <h1 className="text-5xl font-bold text-[#D67C2C] mb-4">Action Plans</h1>
                <p className="text-[#1D3469] text-[18px] sm:text-[20px] font-medium max-w-5xl mx-auto px-4 leading-[1.4]">
                  Convert strategic goals into executable steps with owners, timelines, dependencies, and status tracking.
                </p>
              </div>
            </section>

            <div className="mb-[18px] px-5 py-4 rounded-[16px] bg-[#eef6fc] border border-[#cfe3f3]">
              <div className="text-[13px] text-[#617587] max-w-[980px]">
                Every action item here should be specific enough to execute and measurable enough to monitor.
              </div>
            </div>

            {/* Stats */}
            <section className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
              {[
                { label: "Module Completion", icon: "📌", iconCls: "bg-[#fff4eb] text-[#f47c20] border-[#f7c9a1]", value: `${completion}%`, meta: "Based on action item depth and execution fields", bar: true },
                { label: "Total Action Items", icon: "🛠", iconCls: "bg-[#eef6fc] text-[#2d81c1] border-[#cfe3f3]", value: actions.length, meta: "Execution steps currently defined" },
                { label: "In Progress Items", icon: "▶", iconCls: "bg-[#eaf8f1] text-[#2d9b68] border-[#cfe8dc]", value: inProgress, meta: "Action items actively moving right now" },
                { label: "Execution Readiness", icon: "⚙️", iconCls: "bg-[#fff7e7] text-[#d58d1f] border-[#f4deab]", value: readiness, meta: "How ready the school is to move into monitoring" },
              ].map((s, i) => (
                <div key={i} className="bg-white rounded-xl shadow-md border border-gray-200 p-6 hover:shadow-lg transition-all duration-300 min-h-[130px] flex flex-col justify-between">
                  <div className="flex items-center justify-between gap-[10px] mb-3">
                    <span className="text-[12px] text-[#617587] font-medium">{s.label}</span>
                    <div className={`w-[42px] h-[42px] rounded-[14px] grid place-items-center text-[18px] border ${s.iconCls}`}>{s.icon}</div>
                  </div>
                  <div className="text-[28px] font-bold leading-none text-[#233445] mb-[6px]">{s.value}</div>
                  <div className="text-[12px] text-[#617587]">{s.meta}</div>
                  {s.bar && (
                    <div className="w-full h-[6px] rounded-full bg-[#edf2f8] overflow-hidden mt-2">
                      <div className="h-full rounded-full bg-gradient-to-r from-[#f47c20] to-[#2d81c1] transition-all" style={{ width: `${completion}%` }} />
                    </div>
                  )}
                </div>
              ))}
            </section>

            {/* Tabs */}
            <div className="flex gap-3 mb-[18px]">
              {tabBtn("entry", "Add Action Item")}
              {tabBtn("list", "Action Plan List")}
              {tabBtn("preview", "Live Preview")}
            </div>

            {/* Entry Tab */}
            {activeTab === "entry" && (
              <div className="grid gap-[18px]" style={{ gridTemplateColumns: "1.2fr .8fr" }}>
                <Window
                  title="Action Item Entry"
                  note="Add one execution step at a time and link it to a strategic goal."
                  headerRight={
                    <>
                      <button onClick={() => { setForm(EMPTY_ACTION); setEditingIndex(null); }} className="min-h-[44px] px-4 bg-white text-[#233445] border border-[#e6ecf3] rounded-[12px] text-[13px] font-semibold hover:bg-[#eef6fc] hover:text-[#2d81c1] hover:border-[#cfe3f3] transition-colors">Reset Input</button>
                      <button onClick={handleAddOrUpdate} className="min-h-[44px] px-4 bg-[#f47c20] text-white rounded-[12px] text-[13px] font-semibold hover:bg-[#e46c13] transition-colors">{editingIndex !== null ? "Update Action Item" : "Add Action Item"}</button>
                    </>
                  }
                >
                  <div className="grid grid-cols-2 gap-4">
                    <div className="flex flex-col gap-1">
                      <label className="text-[12px] font-semibold text-[#233445]">Linked Goal</label>
                      <select value={form.goal} onChange={e => setForm(f => ({ ...f, goal: e.target.value }))} className="h-[42px] border border-[#e6ecf3] rounded-[10px] px-3 text-[13px] text-[#233445] bg-white outline-none focus:border-[#2d81c1]">
                        <option value="">Select linked goal</option>
                        {!goals.length ? (
                          <option value="__no_goals__">No goals found — add goals first</option>
                        ) : goals.map((g, i) => <option key={i} value={g.title}>{g.title}</option>)}
                      </select>
                    </div>
                    <div className="flex flex-col gap-1">
                      <label className="text-[12px] font-semibold text-[#233445]">Status</label>
                      <select value={form.status} onChange={e => setForm(f => ({ ...f, status: e.target.value }))} className="h-[42px] border border-[#e6ecf3] rounded-[10px] px-3 text-[13px] text-[#233445] bg-white outline-none focus:border-[#2d81c1]">
                        <option value="">Select status</option>
                        {["Planned","In Progress","Completed","Delayed"].map(s => <option key={s}>{s}</option>)}
                      </select>
                    </div>
                    <div className="flex flex-col gap-1 col-span-2">
                      <label className="text-[12px] font-semibold text-[#233445]">Action Step / Task</label>
                      <input value={form.title} onChange={e => setForm(f => ({ ...f, title: e.target.value }))} placeholder="Example: Create classroom observation rubric and schedule" className="h-[42px] border border-[#e6ecf3] rounded-[10px] px-3 text-[13px] text-[#233445] bg-white outline-none focus:border-[#2d81c1]" />
                    </div>
                    <div className="flex flex-col gap-1 col-span-2">
                      <label className="text-[12px] font-semibold text-[#233445]">Description</label>
                      <textarea value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))} placeholder="Describe what exactly needs to be done..." rows={3} className="border border-[#e6ecf3] rounded-[10px] px-3 py-2 text-[13px] text-[#233445] bg-white outline-none focus:border-[#2d81c1] resize-y" />
                    </div>
                    <div className="flex flex-col gap-1">
                      <label className="text-[12px] font-semibold text-[#233445]">Owner</label>
                      <StaffSelect value={form.owner} onChange={v => setForm(f => ({ ...f, owner: v }))} placeholder="Search staff or type a name…" />
                    </div>
                    <div className="flex flex-col gap-1">
                      <label className="text-[12px] font-semibold text-[#233445]">Support Team / Committee</label>
                      <StaffSelect value={form.support} onChange={v => setForm(f => ({ ...f, support: v }))} placeholder="Search staff or type a committee name…" />
                    </div>
                    <div className="flex flex-col gap-1">
                      <label className="text-[12px] font-semibold text-[#233445]">Start Date</label>
                      <input type="date" value={form.startDate} onChange={e => setForm(f => ({ ...f, startDate: e.target.value }))} className="h-[42px] border border-[#e6ecf3] rounded-[10px] px-3 text-[13px] text-[#233445] bg-white outline-none focus:border-[#2d81c1]" />
                    </div>
                    <div className="flex flex-col gap-1">
                      <label className="text-[12px] font-semibold text-[#233445]">End Date</label>
                      <input type="date" value={form.endDate} onChange={e => setForm(f => ({ ...f, endDate: e.target.value }))} className="h-[42px] border border-[#e6ecf3] rounded-[10px] px-3 text-[13px] text-[#233445] bg-white outline-none focus:border-[#2d81c1]" />
                    </div>
                    <div className="flex flex-col gap-1 col-span-2">
                      <label className="text-[12px] font-semibold text-[#233445]">Dependencies</label>
                      <input value={form.dependencies} onChange={e => setForm(f => ({ ...f, dependencies: e.target.value }))} placeholder="Example: Principal approval, teacher orientation, baseline review" className="h-[42px] border border-[#e6ecf3] rounded-[10px] px-3 text-[13px] text-[#233445] bg-white outline-none focus:border-[#2d81c1]" />
                    </div>
                  </div>
                </Window>

                <Window title="Why action plans matter" note="How this page turns goals into real work.">
                  {[
                    { title: "Goals become executable here", text: "A goal says what must be achieved. An action plan says how that goal will actually move." },
                    { title: "Makes ownership operational", text: "Instead of a broad owner, this page clarifies who does what, when, and with what support." },
                    { title: "Prevents vague implementation", text: "Without action steps, timelines, and dependencies, schools often stay stuck at intent level." },
                    { title: "Feeds monitoring directly", text: "The next module can only monitor what has been defined properly here as an actionable execution plan." },
                  ].map((info, i) => (
                    <div key={i} className="border border-[#e6ecf3] bg-[#fbfcfe] rounded-[14px] p-4 mb-3 last:mb-0">
                      <div className="text-[14px] font-semibold text-[#233445] mb-[6px]">{info.title}</div>
                      <div className="text-[12px] text-[#617587] leading-[1.7]">{info.text}</div>
                    </div>
                  ))}
                </Window>
              </div>
            )}

            {/* Plan List Tab */}
            {activeTab === "list" && (
              <div className="grid grid-cols-2 gap-[18px]">
                <Window title="Execution Cards" note="Quick visual list of all current action steps.">
                  {!actions.length ? (
                    <div className="text-center py-6 text-[13px] text-[#617587]">No action items added yet.</div>
                  ) : (
                    <div className="flex flex-col gap-3">
                      {actions.map((item, i) => (
                        <div key={i} className="border border-[#e6ecf3] bg-[#fbfcfe] rounded-[14px] p-4">
                          <div className="flex justify-between gap-[10px] items-start mb-2">
                            <h4 className="text-[14px] font-semibold text-[#233445]">{item.title}</h4>
                            <Tag cls={statusCls(item.status)}>{item.status}</Tag>
                          </div>
                          <p className="text-[12px] text-[#617587] mb-3">{item.description}</p>
                          <div className="flex gap-2 flex-wrap">
                            <Tag cls="blue">{item.goal || "No Goal"}</Tag>
                            <Tag cls="green">{item.owner || "No Owner"}</Tag>
                            <Tag cls="orange">{formatDateRange(item.startDate, item.endDate)}</Tag>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </Window>

                <Window title="Action Plan Table" note="Editable table for all execution items.">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-4">
                    <div className="flex flex-col gap-1">
                      <label className="text-[11px] font-semibold text-[#617587]">Search</label>
                      <input value={searchQuery} onChange={e => setSearchQuery(e.target.value)} placeholder="Search task, owner, linked goal…" className="h-[38px] border border-[#e6ecf3] rounded-[10px] px-3 text-[12px] text-[#233445] bg-white outline-none focus:border-[#2d81c1]" />
                    </div>
                    <div className="flex flex-col gap-1">
                      <label className="text-[11px] font-semibold text-[#617587]">Status</label>
                      <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)} className="h-[38px] border border-[#e6ecf3] rounded-[10px] px-3 text-[12px] text-[#233445] bg-white outline-none focus:border-[#2d81c1]">
                        <option value="">All Statuses</option>
                        {ACTION_STATUS_OPTIONS.map(s => <option key={s} value={s}>{s}</option>)}
                      </select>
                    </div>
                  </div>
                  <div className="border border-[#e6ecf3] rounded-[18px] overflow-hidden">
                    <table className="w-full border-collapse">
                      <thead>
                        <tr>
                          {["Linked Goal","Task","Owner","Status","Actions"].map(h => (
                            <th key={h} className="bg-[#fafbfd] px-[14px] py-[14px] text-left text-[12px] text-[#617587] font-semibold border-b border-[#e6ecf3]">{h}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {!filteredActions.length ? (
                          <tr><td colSpan={5} className="text-center py-6 text-[13px] text-[#617587]">{actions.length ? "No action items match the current filters." : "No action items added yet."}</td></tr>
                        ) : filteredActions.map((item, i) => (
                          <tr key={i} className="hover:bg-[#fcfdff]">
                            <td className="px-[14px] py-[14px] text-[13px] text-[#233445] border-b border-[#eef2f7]">{item.goal || "-"}</td>
                            <td className="px-[14px] py-[14px] text-[13px] border-b border-[#eef2f7]">
                              <div className="font-semibold text-[#233445] mb-1">{item.title}</div>
                              <div className="text-[12px] text-[#617587]">{item.dependencies || "No dependency added"}</div>
                            </td>
                            <td className="px-[14px] py-[14px] text-[13px] text-[#233445] border-b border-[#eef2f7]">{item.owner || "-"}</td>
                            <td className="px-[14px] py-[14px] text-[13px] border-b border-[#eef2f7]">
                              <Tag cls={statusCls(item.status)}>{item.status || "-"}</Tag>
                            </td>
                            <td className="px-[14px] py-[14px] text-[13px] border-b border-[#eef2f7]">
                              <div className="flex gap-2">
                                <button onClick={() => handleEdit(actions.indexOf(item))} className="px-3 py-1 rounded-[8px] bg-[#eef6fc] text-[#2d81c1] text-[11px] font-semibold hover:bg-[#cfe3f3] transition-colors">Edit</button>
                                <button onClick={() => handleDelete(actions.indexOf(item))} className="px-3 py-1 rounded-[8px] bg-[#fff0f0] text-[#d95353] text-[11px] font-semibold hover:bg-[#f4c5c5] transition-colors">Delete</button>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </Window>
              </div>
            )}

            {/* Preview Tab */}
            {activeTab === "preview" && (
              <div className="grid grid-cols-2 gap-[18px]">
                <Window title="Live Execution Summary" note="What the current action plan structure is showing.">
                  {[
                    { label: "Goal Coverage", value: uniqueGoals.length ? `${actions.length} action item(s) are currently linked across ${uniqueGoals.length} goal(s).` : "No action items saved yet." },
                    { label: "Ownership Snapshot", value: uniqueOwners.length ? `Execution is distributed across ${uniqueOwners.length} owner(s), including ${uniqueOwners.slice(0,3).join(", ")}.` : "No action items saved yet." },
                    { label: "Timeline Snapshot", value: withDates ? `${withDates} action item(s) currently include start and/or end timeline visibility.` : "No action items saved yet." },
                    { label: "Dependency Snapshot", value: withDependencies ? `${withDependencies} action item(s) currently include explicit dependencies or prerequisites.` : "No action items saved yet." },
                  ].map((p, i) => (
                    <div key={i} className="border border-[#e6ecf3] bg-[#fbfcfe] rounded-[14px] p-4 mb-3 last:mb-0">
                      <div className="text-[11px] font-semibold text-[#8ea0b1] uppercase tracking-wider mb-2">{p.label}</div>
                      <div className="text-[13px] text-[#233445]">{p.value}</div>
                    </div>
                  ))}
                </Window>

                <Window title="Readiness Interpretation" note="How ready the school is for monitoring.">
                  {[
                    { title: "Execution quality", text: interpretation },
                    { title: "Why this matters next", text: "Monitoring only works when there are clear action items, owners, dates, and statuses to review." },
                    { title: "Next connected page", text: "After this, move to Monitoring so the school can regularly track progress, blockers, and completion against these action items." },
                  ].map((info, i) => (
                    <div key={i} className="border border-[#e6ecf3] bg-[#fbfcfe] rounded-[14px] p-4 mb-3 last:mb-0">
                      <div className="text-[14px] font-semibold text-[#233445] mb-[6px]">{info.title}</div>
                      <div className="text-[12px] text-[#617587] leading-[1.7]">{info.text}</div>
                    </div>
                  ))}
                </Window>
              </div>
            )}

            <section className="bg-white rounded-xl shadow-md border border-gray-200 p-6 mt-8 mb-8">
              <div className="flex flex-wrap items-center justify-between gap-4">
                <div>
                  <h3 className="text-lg font-semibold text-[#1D3469]">Module Actions</h3>
                  <p className="text-sm text-gray-500">Save and manage module actions from one place.</p>
                </div>
                <div className="flex flex-wrap items-center gap-3">
                  <button onClick={handleSaveDraft} className="border border-gray-300 text-gray-700 px-4 py-2 rounded-lg hover:bg-gray-50 transition-colors font-medium">
                    Save Draft
                  </button>
                  <button onClick={() => { persistActions(actions); showToast("Action plans saved.", "success"); }} className="bg-[#D67C2C] hover:bg-[#c06a25] text-white px-6 py-3 rounded-lg font-medium transition-colors">
                    Save Module
                  </button>
                  <button onClick={handleFillSample} className="border border-[#D67C2C] text-[#D67C2C] px-4 py-2 rounded-lg hover:bg-[#fff4eb] transition-colors font-medium">
                    Load Sample
                  </button>
                  <button onClick={handleClear} className="border border-red-200 text-red-600 px-4 py-2 rounded-lg hover:bg-red-50 transition-colors font-medium">
                    Clear Module
                  </button>
                  <Link to="/sdp/dashboard" className="bg-[#1D3469] hover:bg-[#16284f] text-white px-6 py-3 rounded-lg font-medium transition-colors">
                    Back to Dashboard
                  </Link>
                </div>
              </div>
            </section>
          </main>
        </div>
      </div>

      <ToastContainer />
    </>
  );
}
